import type { Client } from '@efdigital/ll-addon-data';
import { DataTypeSystem } from '@efdigital/ll-addon-data/data-types';
import { filterRegex, page } from '@efdigital/ll-admin-v2/data/json-api.ts';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api.ts';
import { fn } from '@ember/helper';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    client: Client | null;
    onSelect: (client: Client | null) => void;
    readonly?: boolean;
    allowPublic: boolean;
    label: string;
  };
}

export class ClientPicker extends Component<Signature> {
  @service declare store: Store;

  @tracked search = '';
  @tracked searchResults: Client[] = [];
  @tracked showModal = false;

  get client() {
    return this.args.client;
  }

  handleSearchInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.search = target.value;
    void this.performSearch();
  };

  async performSearch() {
    if (!this.search || this.search.length === 0) {
      this.searchResults = [];
      return;
    }

    const filter: Record<string, unknown> = {};

    if (this.search) {
      Object.assign(filter, filterRegex('filter[title]', this.search));
    }

    const response = await this.store.request(
      query<Client>(
        DataTypeSystem.Client,
        {
          ...filter,
          ...page({ offset: 0, limit: 10 }),
          'sort[]': 'title',
        },
        jsonApiOptions
      )
    );

    this.searchResults = response.content.data ?? [];
  }

  selectClient = (client: Client) => {
    this.args.onSelect(client);
    this.showModal = false;
    this.search = '';
    this.searchResults = [];
  };

  clearClient = () => {
    this.args.onSelect(null);
  };

  openModal = () => {
    this.showModal = true;
    this.search = '';
    this.searchResults = [];
  };

  modalClosed = () => {
    this.showModal = false;
    this.search = '';
    this.searchResults = [];
  };

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="client-picker">
        <label
          class="form-label {{unless @allowPublic 'required'}}"
        >Client</label>
        {{#if @readonly}}
          <span
            type="text"
            class="form-control"
            readonly
            value={{if this.client this.client.title "No client selected"}}
            id="{{uniqueId}}-input"
          >{{if this.client this.client.title "No client selected"}}</span>
        {{else}}
          <div class="input-group">
            <span
              type="text"
              class="form-control"
              readonly
              value={{if this.client this.client.title "No client selected"}}
              id="{{uniqueId}}-input"
            >{{if this.client this.client.title "No client selected"}}</span>
            {{#if @allowPublic}}
              {{#if this.client}}
                <button
                  type="button"
                  class="btn btn-outline-primary py-2 btn-sm"
                  {{on "click" this.clearClient}}
                  aria-label="Make public"
                >
                  <iconify-icon inline icon="ph:x" />
                  Make public
                </button>
              {{/if}}
            {{/if}}
            <button
              type="button"
              class="btn btn-primary py-2 rounded-end-1 btn-sm"
              {{on "click" this.openModal}}
              aria-label="Search for client"
            >
              Change Client
            </button>
          </div>
        {{/if}}

        <BsModal
          @open={{this.showModal}}
          @onHidden={{this.modalClosed}}
          @backdropClose={{true}}
          as |modal|
        >
          <modal.header>
            <h4 class="modal-title">Clients</h4>
          </modal.header>
          <modal.body>
            <div class="mb-3">
              <label for="client-search" class="form-label">Search for a client:</label>
              <input
                id="client-search"
                type="search"
                class="form-control"
                placeholder="Search..."
                value={{this.search}}
                {{on "input" this.handleSearchInput}}
              />
            </div>
            {{#if this.searchResults.length}}
              <div class="list-group">
                {{#each this.searchResults as |client|}}
                  <button
                    type="button"
                    class="list-group-item list-group-item-action"
                    {{on "click" (fn this.selectClient client)}}
                  >
                    {{client.title}}
                  </button>
                {{/each}}
              </div>
            {{else if this.search}}
              <p class="text-muted">No clients found matching "{{this.search}}"</p>
            {{/if}}
          </modal.body>
          <modal.footer>
            <button
              type="button"
              class="btn btn-secondary"
              {{on "click" modal.close}}
            >
              Close
            </button>
          </modal.footer>
        </BsModal>
      </div>
    {{/let}}
  </template>
}

export default ClientPicker;
